import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { MdCloudUpload, MdClose } from "react-icons/md";
import styles from "./UserImageUploadModal.module.css";

const UserImageUploadModal = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  defaultGender = "",
}) => {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [gender, setGender] = useState(defaultGender || "women");
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
  const ALLOWED_EXTENSIONS = ["jpg", "jpeg", "png"];

  // Helper to extract display name from filename (e.g., Nidhi.jpg -> Nidhi)
  const extractDisplayName = (fileName) => {
    if (!fileName) return "";
    const lastDotIndex = fileName.lastIndexOf(".");
    if (lastDotIndex === -1) return fileName;
    return fileName.substring(0, lastDotIndex);
  };

  useEffect(() => {
    if (!isOpen) {
      setFile(null);
      setPreviewUrl(null);
      setDisplayName("");
      setGender(defaultGender || "women");
      setIsDragging(false);
      setError("");
    } else {
      setGender(defaultGender || "women");
    }
  }, [isOpen, defaultGender]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && !isLoading) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, isLoading, onClose]);

  if (!isOpen || typeof document === "undefined") return null;

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;

    const extension = selectedFile.name.split(".").pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      setError(`Invalid file format. Please upload JPG, JPEG, or PNG.`);
      return;
    }

    if (selectedFile.size > MAX_FILE_SIZE) {
      setError(`File size exceeds 50MB limit.`);
      return;
    }

    setError("");
    setFile(selectedFile);
    const extractedName = extractDisplayName(selectedFile.name);
    setDisplayName(extractedName);

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setPreviewUrl(null);
    setDisplayName("");
    setError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!file) {
      setError("Please select or drop an image first.");
      return;
    }

    if (!gender) {
      setError("Please select a gender.");
      return;
    }

    const finalDisplayName = displayName.trim() || extractDisplayName(file.name);

    const formData = new FormData();
    formData.append("sName", finalDisplayName);
    formData.append("gender", gender);
    formData.append("sType", "user");
    formData.append("frontImage", file);

    onSubmit(formData);
  };

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.titleWrapper}>
            <h2 className={styles.title}>Upload Your Image</h2>
            <p className={styles.subtitle}>Upload a photo of yourself for virtual try-on</p>
          </div>
          <button className={styles.closeBtn} onClick={onClose} disabled={isLoading}>
            <MdClose size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.body}>
          {!file ? (
            <div
              className={`${styles.dropZone} ${isDragging ? styles.dragActive : ""}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png"
                onChange={handleInputChange}
                className={styles.fileInput}
                disabled={isLoading}
              />
              <div className={styles.uploadIconWrapper}>
                <MdCloudUpload className={styles.uploadIcon} />
              </div>
              <p className={styles.dropTitle}>
                {isDragging ? "Drop your photo here" : "Drag & drop your photo here"}
              </p>
              <p className={styles.dropOr}>or click to browse from device</p>
              <span className={styles.supportedFormats}>Supports JPG, JPEG, PNG (Max 50MB)</span>
            </div>
          ) : (
            <div className={styles.previewSection}>
              <div className={styles.previewBox}>
                <img src={previewUrl} alt="Upload preview" className={styles.previewImage} />
                <button
                  type="button"
                  className={styles.removeBtn}
                  onClick={handleRemoveFile}
                  disabled={isLoading}
                  title="Remove image"
                >
                  <MdClose size={18} />
                </button>
              </div>

              <div className={styles.displayNameText}>
                 <span>{displayName}</span>
              </div>

              <div className={styles.fileDetails}>
                <div className={styles.detailRow}>
                  <label className={styles.label}>Gender</label>
                  <select
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                    className={styles.select}
                    disabled={isLoading}
                  >
                    <option value="women">Women</option>
                    <option value="men">Men</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {error && <div className={styles.errorMessage}>{error}</div>}

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.submitBtn}
              disabled={!file || isLoading}
            >
              {isLoading ? "Uploading..." : "Upload"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default UserImageUploadModal;
